import { TranslationEntityTypeEnum, TranslationSectionEnum, TranslationTabEnum } from '../enums'
import type { TranslationTextField } from '../interfaces'

const { INVENTORY, MODIFIERS, MENUS, KIOSK, SETTINGS } = TranslationTabEnum

const UNIT_FIELDS: TranslationTextField[] = [
  'name',
  'name_plural_1',
  'name_plural_2',
  'name_plural_3',
  'name_plural_4',
  'name_plural_5',
]

export const translationSections: Record<
  TranslationSectionEnum,
  { entityType: TranslationEntityTypeEnum; fields: TranslationTextField[] }
> = {
  [TranslationSectionEnum.INVENTORY_CATEGORIES]: {
    entityType: TranslationEntityTypeEnum.INVENTORY_CATEGORY,
    fields: ['name'],
  },
  [TranslationSectionEnum.INVENTORY_SUB_CATEGORIES]: {
    entityType: TranslationEntityTypeEnum.INVENTORY_CATEGORY,
    fields: ['name'],
  },
  [TranslationSectionEnum.INVENTORY_ITEMS]: {
    entityType: TranslationEntityTypeEnum.INVENTORY_ITEM,
    fields: ['name', 'description', 'allergen'],
  },
  [TranslationSectionEnum.MENUS]: { entityType: TranslationEntityTypeEnum.MENU, fields: ['name', 'description'] },
  [TranslationSectionEnum.MENU_CATEGORIES]: {
    entityType: TranslationEntityTypeEnum.MENU_CATEGORY,
    fields: ['name', 'description'],
  },
  [TranslationSectionEnum.MENU_SUB_CATEGORIES]: {
    entityType: TranslationEntityTypeEnum.MENU_CATEGORY,
    fields: ['name', 'description'],
  },
  [TranslationSectionEnum.MENU_ITEMS]: { entityType: TranslationEntityTypeEnum.MENU_ITEM, fields: ['name'] },
  [TranslationSectionEnum.MODIFIER_GROUPS]: {
    entityType: TranslationEntityTypeEnum.MODIFIER_GROUP,
    fields: ['name', 'description'],
  },
  [TranslationSectionEnum.MODIFIER_ITEMS]: {
    entityType: TranslationEntityTypeEnum.MODIFIER_ITEM,
    fields: ['name', 'description'],
  },
  [TranslationSectionEnum.NON_INVENTORY_ITEMS]: {
    entityType: TranslationEntityTypeEnum.MODIFIER_ITEM,
    fields: ['name', 'description'],
  },
  [TranslationSectionEnum.KIOSK]: {
    entityType: TranslationEntityTypeEnum.KIOSK_PROFILE,
    fields: ['welcome_message', 'slogan'],
  },
  [TranslationSectionEnum.UNITS]: { entityType: TranslationEntityTypeEnum.UNIT, fields: UNIT_FIELDS },
  [TranslationSectionEnum.ITEM_TAGS]: { entityType: TranslationEntityTypeEnum.ITEM_TAG, fields: ['name'] },
  [TranslationSectionEnum.REASONS]: { entityType: TranslationEntityTypeEnum.REASON, fields: ['reason'] },
  [TranslationSectionEnum.SALES_TYPES]: { entityType: TranslationEntityTypeEnum.SALES_TYPE, fields: ['name'] },
}

export const translationTabSections: Record<TranslationTabEnum, TranslationSectionEnum[]> = {
  [INVENTORY]: [
    TranslationSectionEnum.INVENTORY_CATEGORIES,
    TranslationSectionEnum.INVENTORY_SUB_CATEGORIES,
    TranslationSectionEnum.INVENTORY_ITEMS,
  ],
  [MENUS]: [
    TranslationSectionEnum.MENUS,
    TranslationSectionEnum.MENU_CATEGORIES,
    TranslationSectionEnum.MENU_SUB_CATEGORIES,
    TranslationSectionEnum.MENU_ITEMS,
  ],
  [MODIFIERS]: [
    TranslationSectionEnum.MODIFIER_GROUPS,
    TranslationSectionEnum.MODIFIER_ITEMS,
    TranslationSectionEnum.NON_INVENTORY_ITEMS,
  ],
  [KIOSK]: [TranslationSectionEnum.KIOSK],
  [SETTINGS]: [
    TranslationSectionEnum.UNITS,
    TranslationSectionEnum.ITEM_TAGS,
    TranslationSectionEnum.REASONS,
    TranslationSectionEnum.SALES_TYPES,
  ],
}

// NOTE: Languages that the other platforms don't support yet are marked with `isSupported: false`.
// They are hidden in the Translations page and their saved translations are kept until they are supported again.
export const translationLanguages: { label: string; value: string; isSupported: boolean }[] = [
  { label: 'English', value: 'en', isSupported: true },
  { label: 'Spanish', value: 'es', isSupported: true },
  { label: 'French', value: 'fr', isSupported: true },
  { label: 'German', value: 'de', isSupported: true },
  { label: 'Portuguese', value: 'pt', isSupported: true },
  { label: 'Polish', value: 'pl', isSupported: true },
  { label: 'Dutch', value: 'nl', isSupported: true },
  { label: 'Greek', value: 'el', isSupported: true },
  { label: 'Czech', value: 'cs', isSupported: true },
  { label: 'Swedish', value: 'sv', isSupported: true },
  { label: 'Hungarian', value: 'hu', isSupported: true },
  { label: 'Danish', value: 'da', isSupported: true },
  { label: 'Finnish', value: 'fi', isSupported: true },
  { label: 'Slovak', value: 'sk', isSupported: true },
  { label: 'Bulgarian', value: 'bg', isSupported: true },
  { label: 'Croatian', value: 'hr', isSupported: true },
  { label: 'Lithuanian', value: 'lt', isSupported: true },
  { label: 'Latvian', value: 'lv', isSupported: true },
  { label: 'Estonian', value: 'et', isSupported: true },
  { label: 'Slovenian', value: 'sl', isSupported: true },
  { label: 'Irish', value: 'ga', isSupported: true },
  { label: 'Maltese', value: 'mt', isSupported: true },
  { label: 'Welsh', value: 'cy', isSupported: true },
  { label: 'Romanian', value: 'ro', isSupported: true },
  { label: 'Italian', value: 'it', isSupported: true },
  { label: 'Mandarin Chinese', value: 'zh_cn', isSupported: true },
  { label: 'Hindi', value: 'hi', isSupported: true },
  { label: 'Arabic', value: 'ar', isSupported: true },
  { label: 'Bengali', value: 'bn', isSupported: true },
  { label: 'Russian', value: 'ru', isSupported: true },
  { label: 'Japanese', value: 'ja', isSupported: true },
  { label: 'Turkish', value: 'tr', isSupported: true },
  { label: 'Korean', value: 'ko', isSupported: true },
  { label: 'Vietnamese', value: 'vi', isSupported: true },
  { label: 'Indonesian', value: 'id', isSupported: true },
  { label: 'Thai', value: 'th', isSupported: true },
  { label: 'Malay', value: 'ms', isSupported: true },
  { label: 'Egyptian Arabic', value: 'ar_eg', isSupported: true },
  { label: 'Ukrainian', value: 'uk', isSupported: true },
]

export const supportedTranslationLanguages = translationLanguages.filter((language) => language.isSupported)
